import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';
import { 
  Home, 
  Users, 
  Activity, 
  Settings, 
  BarChart3, 
  CreditCard, 
  UserCheck,
  LogOut,
  Menu,
  X,
  FileText,
  Database,
} from 'lucide-react';
import { useState, useEffect, useRef } from 'react';

interface NavItem {
  name: string;
  href?: string;
  icon: React.ComponentType<any>;
  roles?: string[];
  submenu?: NavItem[];
}

const navigation: NavItem[] = [
  { name: 'Dashboard', href: '/', icon: Home },
  { name: 'Members', href: '/members', icon: Users },
  { name: 'Activities', href: '/activities', icon: Activity },
  { name: 'Analytics', href: '/analytics', icon: BarChart3 },
  { name: 'Membership Fees', href: '/membership-fees', icon: CreditCard },
  { name: 'Tracker', href: '/tracker-dashboard', icon: UserCheck, roles: ['admin', 'tracker'] },
  { 
    name: 'Settings', 
    icon: Settings, 
    roles: ['admin'],
    submenu: [
      { name: 'System Settings', href: '/settings', icon: Settings },
      { name: 'Audit Trail', href: '/audit', icon: FileText },
      { name: 'Data Management', href: '/data-management', icon: Database },
    ]
  },
];

export const Navbar: React.FC = () => {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // Use the user context for user information and logout
  const { userAccount, userRole, handleLogout } = useUser();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const filteredNavigation = navigation.filter(item => 
    !item.roles || item.roles.includes(userRole)
  );

  return (
    <nav className="bg-white shadow-sm border-b border-san-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex">
            {/* Desktop Navigation */}
            <div className="hidden lg:ml-4 lg:flex lg:space-x-4">
              {filteredNavigation.map((item) => {
                const hasSubmenu = item.submenu && item.submenu.length > 0;
                const isSubmenuActive = hasSubmenu && item.submenu?.some(subItem => location.pathname === subItem.href);
                const isActive = location.pathname === item.href || isSubmenuActive;
                
                if (hasSubmenu) {
                  return (
                    <div key={item.name} className="relative" ref={dropdownRef}>
                      <button
                        onClick={() => setActiveDropdown(activeDropdown === item.name ? null : item.name)}
                        className={`inline-flex items-center px-1 pt-6 text-sm font-medium border-b-2 min-h-[44px] ${
                          isActive
                            ? 'border-san-green text-san-gray-900'
                            : 'border-transparent text-san-gray-500 hover:text-san-gray-700 hover:border-san-gray-300'
                        }`}
                      >
                        <item.icon className="w-4 h-4 mr-2 flex-shrink-0" />
                        <span className="truncate">{item.name}</span>
                      </button>
                      
                      {activeDropdown === item.name && (
                        <div className="absolute left-0 mt-1 w-48 bg-white shadow-lg rounded-md border border-gray-200 z-50">
                          {item.submenu?.map((subItem) => (
                            <Link
                              key={subItem.name}
                              to={subItem.href!}
                              onClick={() => setActiveDropdown(null)}
                              className={`flex items-center px-4 py-2 text-sm hover:bg-gray-50 ${
                                location.pathname === subItem.href 
                                  ? 'bg-san-green-50 text-san-green border-r-2 border-san-green' 
                                  : 'text-gray-700'
                              }`}
                            >
                              <subItem.icon className="w-4 h-4 mr-3 flex-shrink-0" />
                              {subItem.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }
                
                return (
                  <Link
                    key={item.name}
                    to={item.href!}
                    className={`inline-flex items-center px-1 pt-1 text-sm font-medium border-b-2 min-h-[44px] ${
                      isActive
                        ? 'border-san-green text-san-gray-900'
                        : 'border-transparent text-san-gray-500 hover:text-san-gray-700 hover:border-san-gray-300'
                    }`}
                  >
                    <item.icon className="w-4 h-4 mr-2 flex-shrink-0" />
                    <span className="truncate">{item.name}</span>
                  </Link>
                );
              })}
            </div>
          </div>

          {/* User Menu */}
          <div className="hidden lg:ml-6 lg:flex lg:items-center">
            <div className="flex items-center space-x-3">
              <span className="text-sm text-san-gray-700 truncate max-w-32">
                {userAccount?.name || 'Unknown User'}
              </span>
              <span className="px-2 py-1 text-xs font-medium bg-san-green text-white rounded-full">
                {userRole}
              </span>
              <button
                onClick={handleLogout}
                className="inline-flex items-center text-sm text-san-gray-500 hover:text-san-gray-700 min-h-[44px] px-2"
              >
                <LogOut className="w-4 h-4 mr-1" />
                Logout
              </button>
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden flex items-center">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-san-gray-400 hover:text-san-gray-500 hover:bg-san-gray-100 min-h-[44px] min-w-[44px]"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? (
                <X className="block h-6 w-6" />
              ) : (
                <Menu className="block h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden">
          <div className="pt-2 pb-3 space-y-1">
            {filteredNavigation.map((item) => {
              const hasSubmenu = item.submenu && item.submenu.length > 0;
              const isSubmenuActive = hasSubmenu && item.submenu?.some(subItem => location.pathname === subItem.href);
              const isActive = location.pathname === item.href || isSubmenuActive;
              
              if (hasSubmenu) {
                return (
                  <div key={item.name}>
                    <div className="flex items-center pl-3 pr-4 py-3 text-base font-medium text-san-gray-600">
                      <item.icon className="w-5 h-5 mr-3 flex-shrink-0" />
                      <span className="truncate">{item.name}</span>
                    </div>
                    {item.submenu?.map((subItem) => (
                      <Link
                        key={subItem.name}
                        to={subItem.href!}
                        className={`flex items-center pl-8 pr-4 py-2 text-sm min-h-[44px] ${
                          location.pathname === subItem.href
                            ? 'bg-san-green-50 border-r-4 border-san-green text-san-green-700'
                            : 'text-san-gray-500 hover:text-san-gray-700 hover:bg-san-gray-50'
                        }`}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <subItem.icon className="w-4 h-4 mr-3 flex-shrink-0" />
                        <span className="truncate">{subItem.name}</span>
                      </Link>
                    ))}
                  </div>
                );
              }
              
              return (
                <Link
                  key={item.name}
                  to={item.href!}
                  className={`flex items-center pl-3 pr-4 py-3 text-base font-medium min-h-[44px] ${
                    isActive
                      ? 'bg-san-green-50 border-r-4 border-san-green text-san-green-700'
                      : 'text-san-gray-600 hover:text-san-gray-900 hover:bg-san-gray-50'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <item.icon className="w-5 h-5 mr-3 flex-shrink-0" />
                  <span className="truncate">{item.name}</span>
                </Link>
              );
            })}
          </div>
          <div className="pt-4 pb-3 border-t border-san-gray-200 safe-area-bottom">
            <div className="flex items-center px-4">
              <div className="flex-shrink-0">
                <div className="h-8 w-8 rounded-full bg-san-green flex items-center justify-center">
                  <span className="text-sm font-medium text-white">
                    {userAccount?.name?.charAt(0) || 'U'}
                  </span>
                </div>
              </div>
              <div className="ml-3 min-w-0 flex-1">
                <div className="text-base font-medium text-san-gray-800 truncate">
                  {userAccount?.name || 'Unknown User'}
                </div>
                <div className="text-sm text-san-gray-500 truncate">
                  {userAccount?.username || 'No email'}
                </div>
                <div className="mt-1">
                  <span className="px-2 py-1 text-xs font-medium bg-san-green text-white rounded-full">
                    {userRole}
                  </span>
                </div>
              </div>
            </div>
            <div className="mt-3 space-y-1">
              <button
                onClick={handleLogout}
                className="flex items-center px-4 py-3 text-base font-medium text-san-gray-500 hover:text-san-gray-800 hover:bg-san-gray-100 w-full text-left min-h-[44px]"
              >
                <LogOut className="w-5 h-5 mr-3 flex-shrink-0" />
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};